import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const StaffPage = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'STF', fullName: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get('/staff');
      setStaff(data.data);
    } catch { toast('Failed to load staff', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => { setEditing(null); setForm({ username: '', password: '', role: 'STF', fullName: '', email: '', phone: '' }); setShowModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ username: s.username, password: '', role: s.role, fullName: s.full_name || '', email: s.email || '', phone: s.phone || '' }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing && (!form.username || !form.password)) { toast('Username and password required', 'error'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/staff/${editing.id}`, form);
        toast('Staff updated!', 'success');
      } else {
        await api.post('/staff', form);
        toast('Staff member created!', 'success');
      }
      setShowModal(false);
      fetch();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await api.put(`/staff/${id}`, { isActive: !isActive });
      toast(!isActive ? 'Staff activated' : 'Staff deactivated', 'success');
      fetch();
    } catch { toast('Failed to update', 'error'); }
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>👥 Staff Management</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{staff.filter(s => s.is_active).length} active staff members</p>
        </div>
        <button id="add-staff-btn" className="btn btn-primary" onClick={openAdd}>+ Add Staff</button>
      </div>

      {staff.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">👥</div><p>No staff yet</p><button className="btn btn-primary" onClick={openAdd}>+ Add Staff</button></div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Staff</th><th>Role</th><th>Contact</th><th>Deliveries</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {staff.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--secondary))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15 }}>
                          {(s.full_name || s.username)[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{s.full_name || s.username}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>@{s.username}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={`badge badge-${s.role}`}>{s.role}</span></td>
                    <td>
                      <div style={{ fontSize: 12 }}>{s.email || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.phone || '—'}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{s.active_deliveries || 0} active</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.total_deliveries || 0} total</div>
                    </td>
                    <td>
                      <span style={{ fontSize: 12, fontWeight: 600, color: s.is_active ? 'var(--success)' : 'var(--danger)' }}>
                        {s.is_active ? '● Active' : '○ Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}>✏️</button>
                        <button
                          className={`btn btn-sm ${s.is_active ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => handleToggle(s.id, s.is_active)}
                        >
                          {s.is_active ? '⏸' : '▶'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Staff' : 'Add Staff Member'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="form-grid">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-input" placeholder="Full name" value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                    <option value="STF">STF — Staff</option>
                    <option value="MGR">MGR — Manager</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input className="form-input" placeholder="username" value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} disabled={!!editing} />
                </div>
                <div className="form-group">
                  <label className="form-label">{editing ? 'New Password (optional)' : 'Password *'}</label>
                  <input className="form-input" type="password" placeholder="Password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" placeholder="email@example.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" placeholder="+91 9876543210" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer" style={{ margin: 0, padding: 0, border: 'none', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Create Staff'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPage;
